import React, {FC} from 'react';
import {Route, Routes} from 'react-router-dom';
import {ToastContainer} from 'react-toastify';
import {AppVersionLayer} from 'components/AppVersionLayer';
import {PRMGLayout} from 'components/layout/Layout';
import {browserHistory} from 'routing/browserHistory';
import {HistoryRouter} from 'routing/HistoryRouter';
import {PrivateRoutes, PublicRoutes} from 'routing/ProtectedRoutes';
import {AppRoutePaths, AuthRoutePaths} from 'routing/Routes';
import {EmailCheckResetLinkView} from 'views/auth/EmailCheckResetLinkView';
import {EmailCheckView} from 'views/auth/EmailCheckView';
import {ForgotPasswordView} from 'views/auth/ForgotPasswordView';
import {LoginView} from 'views/auth/LoginView';
import {RegisterView} from 'views/auth/RegisterView';
import {ResetPasswordView} from 'views/auth/ResetPasswordView';
import {SignUpView} from 'views/auth/SignUpView';
import {DashboardView} from 'views/DashboardView';
import {GarageDisplayView} from 'views/garages/GarageDisplayView';
import {GarageEditView} from 'views/garages/GarageEditView';
import {GarageListView} from 'views/garages/GarageListView';
import {NotFoundView} from 'views/NotFoundView';
import {ReservationListView} from 'views/reservations/ReservationListView';
import {ServiceDisplayView} from 'views/serviceActions/services/ServiceDisplayView';
import {ServiceEditView} from 'views/serviceActions/services/ServiceEditView';
import {ServiceListView} from 'views/serviceActions/services/ServiceListView';
import {TaskDisplayView} from 'views/serviceActions/tasks/TaskDisplayView';
import {ServiceTaskEditView} from 'views/serviceActions/tasks/TaskEditView';
import {TaskListView} from 'views/serviceActions/tasks/TaskListView';
import {VehicleDisplayView} from 'views/vehicles/VehicleDisplayView';
import {VehicleEditView} from 'views/vehicles/VehicleEditView';
import {ReservationEditView} from 'views/reservations/ReservationEditView';
import {ReservationDisplayView} from 'views/reservations/ReservationDisplayView';
import {CustomersListView} from './views/users/customers/CustomersListView';
import {EmployeesListView} from './views/users/employees/EmployeesListView';
import {CustomerDisplayView} from './views/users/customers/CustomerDisplayView';
import {CustomerEditView} from './views/users/customers/CustomerEditView';
import {EmployeeDisplayView} from './views/users/employees/EmployeeDisplayView';
import {EmployeeEditView} from './views/users/employees/EmployeeEditView';
import 'react-toastify/dist/ReactToastify.css';
import 'assets/css/app.css';

const App: FC = () => {
  return (
    <div className="App">
      <ToastContainer
        theme="colored"
        autoClose={5000}
        position="bottom-center"
        hideProgressBar={true}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={false}
        className="skip-fade"
      />
      <HistoryRouter history={browserHistory}>
        <Routes>
          <Route element={<PrivateRoutes />}>
            <Route element={<PRMGLayout />}>
              <Route path={AppRoutePaths.dashboard.url} element={<DashboardView />} />
              <Route path={AppRoutePaths.garages.url} element={<GarageListView />} />
              <Route path={AppRoutePaths.garage.url} element={<GarageDisplayView />} />
              <Route path={AppRoutePaths.garageCreate.url} element={<GarageEditView />} />
              <Route path={AppRoutePaths.garageEdit.url} element={<GarageEditView />} />
              <Route path={AppRoutePaths.vehicle.url} element={<VehicleDisplayView />} />
              <Route path={AppRoutePaths.vehicleCreate.url} element={<VehicleEditView />} />
              <Route path={AppRoutePaths.vehicleEdit.url} element={<VehicleEditView />} />
              <Route path={AppRoutePaths.services.url} element={<ServiceListView />} />
              <Route path={AppRoutePaths.service.url} element={<ServiceDisplayView />} />
              <Route path={AppRoutePaths.serviceCreate.url} element={<ServiceEditView />} />
              <Route path={AppRoutePaths.serviceCreateCarId.url} element={<ServiceEditView />} />
              <Route path={AppRoutePaths.serviceEdit.url} element={<ServiceEditView />} />
              <Route path={AppRoutePaths.reservations.url} element={<ReservationListView />} />
              <Route path={AppRoutePaths.tasks.url} element={<TaskListView />} />
              <Route path={AppRoutePaths.task.url} element={<TaskDisplayView />} />
              <Route path={AppRoutePaths.serviceTaskAdd.url} element={<ServiceTaskEditView />} />
              <Route path={AppRoutePaths.editTask.url} element={<ServiceTaskEditView />} />
              <Route path={AppRoutePaths.reservationCreate.url} element={<ReservationEditView />} />
              <Route path={AppRoutePaths.reservationId.url} element={<ReservationDisplayView />} />
              <Route path={AppRoutePaths.reservationEdit.url} element={<ReservationEditView />} />
              <Route path={AppRoutePaths.reservationCreateCarId.url} element={<ReservationEditView />} />
              <Route path={AppRoutePaths.customers.url} element={<CustomersListView />} />
              <Route path={AppRoutePaths.customer.url} element={<CustomerDisplayView />} />
              <Route path={AppRoutePaths.customerCreate.url} element={<CustomerEditView />} />
              <Route path={AppRoutePaths.customerEdit.url} element={<CustomerEditView />} />
              <Route path={AppRoutePaths.employees.url} element={<EmployeesListView />} />
              <Route path={AppRoutePaths.employee.url} element={<EmployeeDisplayView />} />
              <Route path={AppRoutePaths.employeeEdit.url} element={<EmployeeEditView />} />
              <Route path={AppRoutePaths.employeeCreate.url} element={<EmployeeEditView />} />
            </Route>
            <Route path={AppRoutePaths.register.url} element={<RegisterView />} />
          </Route>
          <Route element={<PublicRoutes />}>
            <Route path={AuthRoutePaths.signUp.url} element={<SignUpView />} />
            <Route path={AuthRoutePaths.emailCheck.url} element={<EmailCheckView />} />
            <Route path={AuthRoutePaths.signIn.url} element={<LoginView />} />
            <Route path={AuthRoutePaths.forgotPassword.url} element={<ForgotPasswordView />} />
            <Route path={AuthRoutePaths.emailCheckResetLink.url} element={<EmailCheckResetLinkView />} />
            <Route path={AuthRoutePaths.resetPassword.url} element={<ResetPasswordView />} />
          </Route>
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </HistoryRouter>
      <AppVersionLayer />
    </div>
  );
};

export default App;
